// ============================================
// 프롬프트 빌더 — aiGuide.js에서 책임 분리 (순수 이동)
// ============================================
// 동적 QUERY 프롬프트 조립 + SMS 문구 생성 프롬프트(T6 백엔드 이관 대상).
// 의존: aiPrompts(QUERY_PROMPT_TEMPLATE) + aiDateResolver(substituteDateTemplates).

import { resolvePrompt } from './promptStore.js';
import { substituteDateTemplates } from './aiDateResolver';

// 인텐션 캐시에서 동적 QUERY_PROMPT 생성 //
// queryPromptTemplate: DB 우선 텍스트 주입(미지정 시 상수 QUERY_PROMPT_TEMPLATE fallback — nodeAgent 등 테스트 경로 자동 정합)
export function buildQueryPrompt(intentionCache, bufferSummaries = {}, queryPromptTemplate = resolvePrompt('QUERY_PROMPT_TEMPLATE', '')) {
  let intentionList = '';

  for (const category of Object.keys(intentionCache).sort()) {
    intentionList += `${category}:\n`;

    // cache 구조: { intentions: [...], rules: [...] } (executeGetIntentions에서 set) 또는 array (legacy)
    const entries = Array.isArray(intentionCache[category])
      ? intentionCache[category]
      : (intentionCache[category]?.intentions || []);

    for (const i of entries) {
      if (i.priorEndpoint) continue;  // 선행 API 필요 인텐션은 단독 호출 불가 → SQL에서 제외

      const params = typeof i.params === 'string' ? JSON.parse(i.params || '[]') : (i.params || []);
      const hasDateParams = params.some(p => p.name === 'startDate' || p.name === 'endDate');
      const dateSuffix = hasDateParams ? '(startDate, endDate)' : '';
      const desc = (i.description || '').slice(0, 80);

      const af = typeof i.availableFields === 'string' ? JSON.parse(i.availableFields || '{}') : (i.availableFields || {});
      const fieldSample = Object.keys(af).slice(0, 3).join(', ');
      const fieldHint = fieldSample ? ` [fields: ${fieldSample}]` : '';
      intentionList += `  ${i.intentionKey}${dateSuffix} — ${desc}${fieldHint}\n`;
    }

    intentionList += '\n';
  }

  // 버퍼 상태 문자열 생성 //
  const slotEntries = Object.entries(bufferSummaries);
  let bufferState;

  if (slotEntries.length === 0) {
    bufferState = 'Empty (no data loaded)';
  }
  else {
    bufferState = slotEntries.map(([name, info]) => {
      const fields = (info.fields || []).slice(0, 8).join(', ');
      const dr = info.dateRange?.startDate && info.dateRange?.endDate
        ? `, dateRange: ${info.dateRange.startDate}~${info.dateRange.endDate}`
        : '';
      const af = info.appliedFilters?.length > 0
        ? `, filters: ${info.appliedFilters.map(f => `${f.field}=${JSON.stringify(f.condition?.value ?? f.condition?.type)}`).join(' AND ')}`
        : '';
      return `slot "${name}": ${info.intention} (${info.count}건, fields: ${fields}${dr}${af})`;
    }).join('\n');
  }

  const filled = queryPromptTemplate
    .replace('{INTENTIONS}', intentionList)
    .replace('{BUFFER_STATE}', bufferState);
  // 예시 SQL의 {today-N}/{today}/{year-N} 등을 실제 값으로 사전 치환 → LLM에 깨끗한 입력
  return substituteDateTemplates(filled);
}

// SMS 템플릿 프롬프트 (bulk/individual) — 동적 인자 보간 함수 //
export const SMS_TEMPLATE_PROMPT = {
  bulk: (storeName, targetDescription, memberCount, tone) =>
    `"${storeName}" 피트니스 센터 관리자가 ${targetDescription} 회원 ${memberCount}명에게 보낼 문자를 작성해야 합니다.
문자 템플릿 3개를 작성하세요.

규칙:
- 각 템플릿은 2~3줄이어야 합니다. 각 줄 사이에 \n으로 구분.
- 예시: 센터명입니다.\n안내문입니다.\n감사합니다.
- 말투/톤: ${tone || '친근하고 따뜻한 톤'}
- 센터명 포함
- 이모지 사용 금지
- JSON 배열만 반환: [{"text":"첫줄\n둘째줄\n셋째줄"},{"text":"첫줄\n둘째줄\n셋째줄"},{"text":"첫줄\n둘째줄\n셋째줄"}]`,

  individual: (storeName, targetDescription, members, tone) =>
    `"${storeName}" 피트니스 센터 관리자가 ${targetDescription} 회원들에게 개별 맞춤 문자를 작성해야 합니다.
회원별로 이름을 포함한 맞춤 문자를 작성하세요.

회원 목록:
${members.map((m, i) => `${i + 1}. ${m.name}`).join('\n')}

규칙:
- 각 문자는 2~3줄이어야 합니다. 각 줄 사이에 \n으로 구분.
- 예시: 안녕하세요, 홍길동님.\n센터 소식입니다.\n감사합니다.
- 회원 이름을 첫 줄에 포함
- 말투/톤: ${tone || '친근하고 따뜻한 톤'}
- 센터명 포함
- 이모지 사용 금지
- JSON 배열만 반환: [{"name":"회원이름","text":"이름님입니다.\n안내문입니다.\n감사합니다."},...]`,
};
